import { useAuthenticator } from "@aws-amplify/ui-react";
import { fetchAuthSession, JWT } from "aws-amplify/auth";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getInfoCredits } from "../api/broker";
import { environment } from "../config";
import { getUserProfile, loginSuccess, postLogRegisterActivity } from "../controllers/operaciones";
import { getCategories } from "../data/categorias";
import { hasPermission, PagePermissions } from "../data/roles";
import { getAllUmmiappIds, getUmmiapps } from "../data/ummiapps";
import { iCompleteUser } from "../interfaces/Auth/iCompleteUser";
import { iUser } from "../interfaces/Auth/iUser";
import { AuthContextProps, AuthMode } from "../interfaces/SuperAdmin/ContextProps/iAuth";
import { DynamoUser } from "../interfaces/SuperAdmin/Others/DynamoUser";
import { getWhoami } from "../services/api";
import { getToken, getRawToken, removeToken, setToken } from "../utils/tokenFunctions";
import { toast } from "react-toastify";
import { Estado } from "../interfaces/SuperAdmin/Others/iDataEstado";
import { iLTIUserSchoologyContext } from "../interfaces/Auth/iLTIUserSchoologyContext";
import { setGlobalErrorUser } from "../setupGlobalErrorHandlers";
import { connectSocket, disconnectSocket } from "../services/sockets";

const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);

const AuthProvider = ({ children }: { children: React.ReactNode }) => {

  const { authStatus, signOut, error } = useAuthenticator();
  const [authMode, setAuthMode] = useState<AuthMode>("COGNITO");

  const [user, setUser] = useState<iUser | null>(null);
  const [activeRoleId, setActiveRoleId] = useState<string | null>(null);
  const [isLoadingLogin, setIsLoadingLogin] = useState(false);
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isProcessingLTI, setIsProcessingLTI] = useState(false);  

  const { REACT_APP_LOG_USER_REPORTS } = environment;
  const isLogReportsAvailable = REACT_APP_LOG_USER_REPORTS === "true";

  const loginFromLTI = async (ltiContext: iLTIUserSchoologyContext) => {
    if (isProcessingLTI || isInitialized) {
      console.log("LTI ya en proceso o inicializado, saltando...");
      return;
    }

    if(ltiContext && Object.keys(ltiContext).length === 0) {
      console.log("Contexto LTI vacío, no se puede continuar");
      return;
    }

    setIsProcessingLTI(true);
    try {
      setAuthMode("LTI");
      await handleLoginLTI(ltiContext);
    } catch (e: any) {
      toast.error(e?.message || "No fue posible autenticar al usuario (LTI)");
      cleanAccess();
    } finally {
      setIsLoadingLogin(false);
      setIsProcessingLTI(false);
    }
  };

  // Función para hacer el fetch de los créditos que tiene disponible en el mes el usuario
  const monthlyCredits = async (id: string, id_organizacion?: string) => {
    try {
      const res = await getInfoCredits(id, id_organizacion);

      if (res.data.statusCode === 200) {
        return res.data.body;
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Función para actualizar los créditos usados y disponibles del objeto
  const updateCreditInfo = async (id_organizacion?: string) => {
    if (!user) return;

    const currentMonth = await monthlyCredits(user.id, id_organizacion);
    setUser((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        creditosUsados: currentMonth?.creditos_gastados_mes ?? 0,
        creditosDisponibles: currentMonth?.creditos_disponibles ?? 0,
      };
    });
  };

  // Función para crear el objeto User con créditos mensuales, apps, categorías, etc.
  const assignUserData = async (currentUser: iCompleteUser, dynamoUser: DynamoUser, accessToken: JWT | undefined) => {
    try {
      const creditsInfo = await monthlyCredits(currentUser.id);

      const { id, email, name, picture, id_organizacion, organizaciones, sub, roles, request_pais, estado } = currentUser;

      const savedRoleId = localStorage.getItem(`ummia_selected_role_${id}`);
      let activeRole = roles.find((r) => r.id_rol === savedRoleId);

      if (!activeRole) {
          activeRole = roles.find((r) => r.rol === "Admin de Dominio") ??
                       roles.find((r) => r.id_organizacion === id_organizacion) ??
                       roles[0];
      }

      const rawApps = (activeRole && activeRole.ummiapps && activeRole.ummiapps.length > 0)
        ? activeRole.ummiapps
        : currentUser.ummiapps;

      const adminRoles = ["Admin de Dominio", "Admin de Organización"];
      const isAdminRole = activeRole && adminRoles.includes(activeRole.rol);

      // Solo los admins sin apps asignadas ven todas las del país, un usuario normal sin apps ve vacío
      const appsToUse = (rawApps && rawApps.length > 0)
        ? rawApps
        : (isAdminRole ? getAllUmmiappIds() : []);
      const ummiapps = getUmmiapps(appsToUse as any, request_pais);
      const categories = getCategories(ummiapps);

    //Se comenta constante por test de eslint " warning  'token' is assigned a value but never used"  const token = accessToken?.toString();

      return {
        id,
        isActive: dynamoUser.isActive,
        email: email ?? dynamoUser.email,
        picture,
        name: name ?? dynamoUser.name,
        creditosUsados: creditsInfo?.creditos_gastados_mes ?? 0,
        creditosDisponibles: creditsInfo?.creditos_disponibles ?? 0,
        suscripcionesActivas: creditsInfo?.suscripciones ?? [],
        pais: request_pais,
        roles: roles/* .filter((rol)=>(rol.rol !== "Admin de Dominio")) */,
        ummiapps,
        categories,
        rootUmmiapps: currentUser.ummiapps,
        sub,
        id_organizacion,
        organizaciones: organizaciones ?? [],
        estado,
      };
    } catch (error) {
      console.error(error);
      throw new Error("No se pudo extraer la información de créditos mensuales");
    }
  };

  //Función para actualizar la información del usuario con la configuración en el perfil
  const enrichUserWithPerfil = async (userId: string) => {
  try {
    const perfil = await getUserProfile(userId);
    const especialidades = Array.isArray(perfil?.especialidades)
      ? perfil?.especialidades
      : [];

    const perfilId = perfil?.id;

    setUser((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        especialidades,
        ...(perfilId ? { perfilId } : {}),
      };
    });
  } catch (e) {
    console.error("No se pudo cargar perfil para enriquecer AuthContext", e);
  }
};

  // Función específica para autenticación LTI (sin validaciones extra)
  const processUserLoginLTI = async (currentUser: iCompleteUser, accessToken?: JWT, ltiContext?: iLTIUserSchoologyContext) => {
    const dynamoUser = await loginSuccess(currentUser.email);
    if (!dynamoUser) return showError("No se encontró un usuario en DynamoDB");
    // Creamos el objeto user con apps, roles, etc.
    localStorage.setItem("lastLoginTime", new Date().getTime().toString());
    const newUser = await assignUserData(currentUser, dynamoUser, accessToken);
    setUser((prevUser) => {
      return {
        ...newUser,
        ltiContext
      };
    });
    await enrichUserWithPerfil(newUser.id);
    setIsUserAuthenticated(true);
    setIsInitialized(true);
    connectSocket();
  };

  /* Función compartida para procesar usuario después de obtener datos */
  const processUserLogin = async (currentUser: iCompleteUser, accessToken?: JWT) => {
    const estadoRDS = currentUser.estado; // El estado del usuario en RDS

    if (estadoRDS) {
      /* Si está Inactivo */
      if (estadoRDS === Estado.INACTIVO) {
        return showError("Tu cuenta ha sido deshabilitada. Contacta con soporte");
      }

      /* Si está Eliminado */
      if (estadoRDS === Estado.ELIMINADO) {
        return showError("Tu cuenta ha sido eliminada. Contacta con soporte");
      }
    }

    // Buscamos el usuario de DynamoDB con este correo
    const dynamoUser = await loginSuccess(currentUser.email);
    console.log(dynamoUser);

    if (!dynamoUser) return showError("No se encontró un usuario en DynamoDB");

    // Verificamos si el usuario está activo en DynamoDB
    if (!dynamoUser.isActive) return showError("Tu cuenta ha sido deshabilitada. Contacta con soporte");

    // Creamos el objeto user con apps, roles, etc.
    const newUser = await assignUserData(currentUser, dynamoUser, accessToken);

    /* Si el usuario NO tiene suscripciones activas */
    if (!newUser.suscripcionesActivas.length) {
      return showError("Lo siento, no cuentas con suscripciones activas");
    }

    /* Si el usuario NO está asociado a una organización */
    if (!newUser.id_organizacion) {
      return showError("Lo siento, no estás asociado a una organización");
    }

    /* Si el usuario NO tiene roles */
    if (!newUser.roles.length) {
      return showError("Lo siento, no tienes roles asignados");
    }

    console.log(newUser);

    setUser(newUser);
    await enrichUserWithPerfil(newUser.id);
    setIsUserAuthenticated(true);
    setIsInitialized(true);

    // Actualizamos la última vez que se hizo un login
    localStorage.setItem("lastLoginTime", new Date().getTime().toString());
  };

  /* Función específica para autenticación LTI */
  const handleLoginLTI = async (ltiContext: iLTIUserSchoologyContext) => {
    console.log("Inicia handleLoginLTI");
    console.log("isInitialized: " + isInitialized);
    let token = getRawToken();
    if (!token) {
      return showError("No se encontró un token de acceso");
    }

    setIsLoadingLogin(true);
    try {
      // En modo LTI, el token ya está en localStorage
      console.log("Modo LTI: usando token existente");

      //Obtenemos el usuario del getWhoAmI
      const whoAmIRes = await getWhoami();
      const currentUser = whoAmIRes.data;
      console.log(currentUser);
      if (!currentUser) return showError("No se encontró un usuario en el getWhoAmI");

      await processUserLoginLTI(currentUser, token as unknown as JWT, ltiContext);
    } catch (e: any) {
      console.log("Falla handleLoginLTI");
      showError(e?.message || "No fue posible autenticar al usuario");
    } finally {
      setIsLoadingLogin(false);
    }
  };

  /* Esta función ocurre después del signIn de Cognito, y extrae la información del usuario en RDS y DynamoDB */
  const handleLogin = async () => {
    console.log("Inicia handleLogin");
    console.log("isInitialized: " + isInitialized);

    setIsLoadingLogin(true);
    try {
      // Obtenemos el token de acceso de cognito
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken;
      if (!accessToken) {
        return showError("No se encontró un token de acceso");
      }
      setToken(accessToken);

      //Obtenemos el usuario del getWhoAmI
      const whoAmIRes = await getWhoami();
      const currentUser = whoAmIRes.data;
      console.log(currentUser);
      if (!currentUser) return showError("No se encontró un usuario en el getWhoami");

      await processUserLogin(currentUser, accessToken);
      connectSocket();
    } catch (e: any) {
      console.log("Falla handleLogin");
      showError(e?.message || "No fue posible autenticar al usuario");
    } finally {
      setIsLoadingLogin(false);
    }
  };

  // Control de qué se hace en base a authStatus
  useEffect(() => {
    const isLoggedIn = authStatus === "authenticated";
    const isLoggedOut = authStatus === "unauthenticated";
    console.log("authStatus: " + authStatus);

    /* Si el estado es 'autenticado' y NO esta haciendo logout, ejecutamos el handleLogin (solo en modo COGNITO) */
    if (isLoggedIn && !isInitialized && !isLoggingOut && authMode === "COGNITO") {
      handleLogin();
    }

    /* Una vez que el usuario ya sea desautenticado, cambiamos el estado */
    if (isLoggedOut) {
      setIsLoggingOut(false);
    }
  }, [authStatus, isInitialized, authMode]);

  useEffect(() => {
    const token = getToken();
    // Si tengo token en localStorage pero Cognito me marca como no autenticado → es flujo LTI
    if (token && !isInitialized && !isLoggingOut && !isProcessingLTI && authStatus === "unauthenticated") {
      console.log("Detectado flujo LTI, autenticando usuario...");
      const savedContext = localStorage.getItem("ltiContext");
      loginFromLTI(savedContext ? JSON.parse(savedContext) : {});
    }
  }, [authStatus, isInitialized, isLoggingOut, isProcessingLTI, loginFromLTI]);


  /* Función para quitar limpiar los estados agregados en el frontend al iniciar sesión */
  const cleanAccess = useCallback(() => {
    disconnectSocket();
    removeToken();
    setUser(null);
    setIsUserAuthenticated(false);
    setIsInitialized(false);
    setIsProcessingLTI(false);
  }, []);

  /* Toast con mensaje de error personalizado y limpieza de front */
  const showError = async (msg: string) => {
    toast.error(msg);
    cleanAccess();
    signOut();
  };

  const logout = async () => {
    setIsLoggingOut(true);
    /* Registramos el Logout */
    if (user && isLogReportsAvailable) {
      await postLogRegisterActivity(user.id, "LOGOUT_SUCCESSFUL");
    }
    cleanAccess();
    setGlobalErrorUser(undefined);
    signOut();
  };

  /* Función que determina si un usuario puede acceder a una sección en base a sus roles y permisos */
  const canAccess = useCallback(
    (permissions: PagePermissions | PagePermissions[]) => {
      if (!user) return false;
      // Si hay un rol activo seleccionado, evaluar SOLO ese rol
      const rolesToCheck = activeRoleId
        ? user.roles?.filter(r => r.id_rol === activeRoleId) ?? []
        : user.roles;
      return hasPermission(rolesToCheck, permissions);
    },
    [user, activeRoleId]
  );

  /* Actualizamos ummiapps y categorías si el rol activo cambia sin recargar la página */
  useEffect(() => {
    if (!activeRoleId || !isUserAuthenticated) return;

    setUser((prevUser) => {
      if (!prevUser) return prevUser;

      const activeRole = prevUser.roles.find((r) => r.id_rol === activeRoleId);
      if (!activeRole) return prevUser;

      const adminRoles = ["Admin de Dominio", "Admin de Organización"];
      const isAdminRole = adminRoles.includes(activeRole.rol);

      const rawApps =
        activeRole.ummiapps && activeRole.ummiapps.length > 0
          ? activeRole.ummiapps
          : prevUser.rootUmmiapps || [];

      // Solo los admins sin apps ven todas las del país, un usuario normal sin apps ve vacío
      const appsToUse = (rawApps && rawApps.length > 0)
        ? rawApps
        : (isAdminRole ? getAllUmmiappIds() : []);

      const newUmmiapps = getUmmiapps(appsToUse as any, prevUser.pais);
      const newCategories = getCategories(newUmmiapps);

      if (
        JSON.stringify(prevUser.categories) === JSON.stringify(newCategories) &&
        JSON.stringify(prevUser.ummiapps) === JSON.stringify(newUmmiapps)
      ) {
        return prevUser;
      }

      return {
        ...prevUser,
        ummiapps: newUmmiapps,
        categories: newCategories,
      };
    });
  }, [activeRoleId, isUserAuthenticated]);


  /* Siempre que haya un error de Cognito, lo mostramos en toast */
  useEffect(() => {
    if (error) {
      console.log(error);
      // Mapear el mensaje de Cognito a algo más amigable
      if (error.includes("User is disabled")) {
        toast.error("Tu cuenta ha sido deshabilitada. Contacta con soporte");
      } else if (error.includes("Incorrect username or password")) {
        toast.error("Usuario o contraseña incorrectos");
      } else {
        toast.error(error);
      }
    }
  }, [error]);

  // Función para actualizar el contexto LTI del objeto usuario  

  useEffect(() => {
    console.log("AUTHSTATUS: " + authStatus);
  }, [authStatus]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoadingLogin,
        isUserAuthenticated,
        logout,
        setUser,
        canAccess,
        updateCreditInfo,        
        loginFromLTI,
        authMode,
        activeRoleId,
        setActiveRoleId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuthContext = () => useContext(AuthContext);

export { AuthProvider, useAuthContext };
